// PropsExpander — inline per-game panel inside the PickerPanel that fetches
// player props on demand (uses backend 6-hour cache) and lets users add
// over/under legs to the parlay slip.

import React, { useState } from "react";
import axios from "axios";
import { Loader2, Plus, Check, Zap } from "lucide-react";
import { toast } from "sonner";
import { useParlay, MAX_LEGS } from "@/lib/parlay";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ACCENT = "#067647";

function decToAmerican(dec) {
  if (!dec || dec <= 1) return null;
  const v = dec >= 2 ? Math.round((dec - 1) * 100) : Math.round(-100 / (dec - 1));
  return v > 0 ? `+${v}` : `${v}`;
}

/** Collapse the raw props list into player → [{over, under, point}] groups. */
function groupProps(legs) {
  const byPlayer = new Map();
  for (const leg of legs || []) {
    const k = `${leg.market_key}|${leg.player}|${leg.point}`;
    if (!byPlayer.has(k)) {
      byPlayer.set(k, {
        key: k,
        market_key: leg.market_key,
        market_label: leg.market_label,
        player: leg.player,
        point: leg.point,
        over: null,
        under: null,
      });
    }
    byPlayer.get(k)[leg.side] = leg;
  }
  return Array.from(byPlayer.values()).sort((a, b) => a.player.localeCompare(b.player));
}

function propLegFromRow(game, row, side, propLeg) {
  return {
    kind: "prop",
    sport: game.sport,
    home_team: game.home_team,
    away_team: game.away_team,
    date: game.date || null,
    time: null,
    side,
    event_id: propLeg.event_id,
    market_key: row.market_key,
    market_label: row.market_label,
    player: row.player,
    point: row.point,
    odds_decimal: propLeg.odds_decimal,
    implied_prob: propLeg.implied_prob,
  };
}

function keyFor(leg) {
  return [
    "prop",
    (leg.sport || "").toLowerCase(),
    (leg.event_id || "").toLowerCase(),
    (leg.market_key || "").toLowerCase(),
    (leg.player || "").toLowerCase(),
    String(leg.point ?? ""),
    (leg.side || "").toLowerCase(),
  ].join("|");
}

export default function PropsExpander({ game }) {
  const parlay = useParlay();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [props, setProps] = useState(null);
  const [error, setError] = useState(null);

  async function toggle() {
    if (open) { setOpen(false); return; }
    setOpen(true);
    if (props) return;
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${API}/betting/props`, {
        sport: game.sport,
        home_team: game.home_team,
        away_team: game.away_team,
        date: game.date || null,
      }, { timeout: 25000 });
      setProps(r.data);
      if (r.data?.error === "quota") {
        toast.error("Props temporarily unavailable (Odds API quota).");
      } else if ((r.data?.legs || []).length === 0 && !r.data?.cached) {
        toast("No player props posted for this game yet.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not load props.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  }

  function addProp(row, sideKey) {
    const propLeg = row[sideKey];
    if (!propLeg) return;
    const leg = propLegFromRow(game, row, sideKey, propLeg);
    const k = keyFor(leg);
    const existing = parlay.legs.find((l) => keyFor(l) === k);
    if (existing) {
      parlay.removeLeg(leg);
      toast(`Removed ${row.player} ${row.market_label} ${sideKey} ${row.point} from slip.`);
      return;
    }
    // Swap the opposite side if the user already picked over/under on this row
    const otherSideKey = sideKey === "over" ? "under" : "over";
    const otherLeg = row[otherSideKey] ? propLegFromRow(game, row, otherSideKey, row[otherSideKey]) : null;
    if (otherLeg && parlay.legs.some((l) => keyFor(l) === keyFor(otherLeg))) {
      parlay.removeLeg(otherLeg);
    }
    if (parlay.legs.length >= MAX_LEGS) {
      toast.error(`Parlay slip is full (max ${MAX_LEGS} legs).`);
      return;
    }
    parlay.addLeg(leg);
    toast.success(
      `Added ${row.player} ${row.market_label} ${sideKey.toUpperCase()} ${row.point} to slip.`,
      { description: `${Math.min(parlay.legs.length + 1, MAX_LEGS)}/${MAX_LEGS} legs` }
    );
  }

  const grouped = groupProps(props?.legs);

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-2.5 py-1 font-mono text-[9px] tracking-[0.2em] uppercase font-bold border border-[#111] text-[#111] hover:bg-[#111] hover:text-white transition-colors focus-ring"
        data-testid="props-toggle"
      >
        <Zap size={10} strokeWidth={2.6} />
        {open ? "Hide props" : "Props"}
      </button>
      {open && (
        <div
          className="mt-2 border-2 border-[#111] bg-white"
          data-testid="props-panel"
        >
          <div className="px-3 py-1.5 border-b-2 border-[#111] bg-[#fafaf9] flex items-center justify-between">
            <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-[#555] font-bold">
              Player props · {props?.markets?.map((m) => m.replace("player_", "").replace(/_/g, " ")).join(" / ") || "loading"}
            </span>
            {props?.quota_remaining !== undefined && !props?.cached && (
              <span className="font-mono text-[8px] tracking-[0.15em] uppercase text-[#888]">
                {props.quota_remaining} credits left
              </span>
            )}
            {props?.cached && (
              <span className="font-mono text-[8px] tracking-[0.15em] uppercase text-[#888]">
                cached
              </span>
            )}
          </div>
          <div className="divide-y divide-[#eee] max-h-[260px] overflow-y-auto">
            {loading && (
              <div className="p-4 flex items-center justify-center font-mono text-[9px] tracking-[0.25em] uppercase text-[#555] font-bold gap-2">
                <Loader2 size={12} className="spin-slow" />
                Fetching player lines…
              </div>
            )}
            {!loading && error && (
              <div className="p-3 font-mono text-xs text-[#c0272d] tracking-[0.05em] font-bold">
                {error}
              </div>
            )}
            {!loading && !error && grouped.length === 0 && (
              <div className="p-3 text-center font-mono text-[9px] tracking-[0.25em] uppercase text-[#555]">
                No props available yet
              </div>
            )}
            {!loading && grouped.map((row, i) => {
              const overActive = row.over && parlay.legs.some((l) =>
                keyFor(l) === keyFor(propLegFromRow(game, row, "over", row.over))
              );
              const underActive = row.under && parlay.legs.some((l) =>
                keyFor(l) === keyFor(propLegFromRow(game, row, "under", row.under))
              );
              return (
                <div
                  key={row.key}
                  className="px-3 py-2 flex items-center justify-between gap-3 hover:bg-[#fafaf9]"
                  data-testid={`props-row-${i}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-extrabold text-sm text-[#111] leading-tight truncate">
                      {row.player}
                    </div>
                    <div className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#555] font-bold">
                      {row.market_label} · {row.point}
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    {row.over && (
                      <button
                        type="button"
                        onClick={() => addProp(row, "over")}
                        aria-pressed={overActive}
                        data-testid={`props-add-over-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-1 font-mono text-[9px] tracking-[0.1em] uppercase font-extrabold border-2 transition-colors focus-ring"
                        style={{
                          background: overActive ? ACCENT : "transparent",
                          color: overActive ? "#fff" : "#111",
                          borderColor: overActive ? ACCENT : "#111",
                        }}
                      >
                        {overActive ? <Check size={10} strokeWidth={3} /> : <Plus size={10} strokeWidth={3} />}
                        O {decToAmerican(row.over.odds_decimal)}
                      </button>
                    )}
                    {row.under && (
                      <button
                        type="button"
                        onClick={() => addProp(row, "under")}
                        aria-pressed={underActive}
                        data-testid={`props-add-under-${i}`}
                        className="inline-flex items-center gap-1 px-2 py-1 font-mono text-[9px] tracking-[0.1em] uppercase font-extrabold border-2 transition-colors focus-ring"
                        style={{
                          background: underActive ? ACCENT : "transparent",
                          color: underActive ? "#fff" : "#111",
                          borderColor: underActive ? ACCENT : "#111",
                        }}
                      >
                        {underActive ? <Check size={10} strokeWidth={3} /> : <Plus size={10} strokeWidth={3} />}
                        U {decToAmerican(row.under.odds_decimal)}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
